<script setup>
import facebook from "@/assets/images/components/footer/jt-facebook.png";
import instagram from "@/assets/images/components/footer/jt-instagram.png";
import youtube from "@/assets/images/components/footer/jt-youtube.png";
import linkedin from "@/assets/images/components/footer/jt-linkedin.png";

import { NuxtLink } from "#components";

const nuxtApp = useNuxtApp();
const localePath = useLocalePath();
const { locale } = useI18n();
const { openModal, isOpen } = useExternalLinkModal();
const { fetchMedias } = useFetchAPI();

const props = defineProps(["footerData"]);

const showDialogue = ref(false);
const currentModal = ref(null);
const ruleIsLink = ref(/^(\/|https?:\/\/)/i);

const getImageSrc = (name) => {
    const map = {
        facebook,
        instagram,
        youtube,
        linkedin,
    };
    return map[name];
};

const handleExternalLinkClick = (link) => {
    if (link.type === "linkExternal") openModal(link.href);
};

const sitemapBindings = computed(() =>
    props.footerData?.sitemap?.data?.map((item) => {
        if (ruleIsLink.value.test(item.href)) {
            return {
                bindings: {
                    to:
                        item.type === "linkExternal"
                            ? "#"
                            : localePath(item.href),
                    external: undefined,
                    target: undefined,
                },
                onClick:
                    item.type === "linkExternal"
                        ? () => handleExternalLinkClick(item)
                        : null,
            };
        }
        return {
            bindings: {},
            onClick: () => openSitemapModal(item.href),
        };
    }),
);

const preventScroll = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

const externalImagesIds = computed(() => {
    const menuLinks =
        nuxtApp.payload.data?.["fetchPage-menu"]?.[locale.value]?.meta_box
            ?.links || [];
    const sitemapData = props.footerData?.sitemap?.data || [];

    return [
        ...menuLinks.map((item) => item?.hyperImage?.[0]),
        ...sitemapData.map((item) => item?.hyperImage?.[0]),
    ]
        .filter((id) => id)
        .filter((id, index, array) => array.indexOf(id) === index);
});

watch(
    isOpen,
    (newValue) => {
        if (newValue && externalImagesIds.value.length > 0) {
            fetchMedias(externalImagesIds.value, "external");
        }
    },
    { once: true },
);

const openSitemapModal = (href) => {
    if (!href || ruleIsLink.value.test(href)) return;
    currentModal.value = href;
    showDialogue.value = true;
    document.body.style.overflow = "hidden";
    document.body.addEventListener("wheel", preventScroll, { passive: false });
};

const closeDialogue = () => {
    showDialogue.value = false;
    currentModal.value = null;
    document.body.style.overflow = "auto";
    document.body.removeEventListener("wheel", preventScroll);
};
</script>

<template>
    <footer
        class="relative z-[300] w-full overflow-hidden bg-base-black p-[4.07vw] pt-8 sm:p-[5.2vw] sm:pt-8 lg:px-[2.64vw] lg:pb-12 lg:pt-16"
    >
        <!-- Dialogue -->
        <teleport to="body">
            <Transition name="fade">
                <LazyBaseDialogueVisitUs
                    v-if="showDialogue && currentModal === 'VisitUs'"
                    @close="closeDialogue"
                />
            </Transition>
        </teleport>

        <teleport to="body">
            <Transition name="fade">
                <LazyBaseDialogueUnderLine
                    v-if="showDialogue && currentModal === 'UnderLine'"
                    @close="closeDialogue"
                />
            </Transition>
        </teleport>

        <div class="flex flex-col lg:flex-row lg:justify-between">
            <!-- slt-container -->
            <!-- sns -->
            <section class="mb-12 flex gap-2">
                <template
                    v-for="item in footerData?.sns?.data"
                    :key="item?.name"
                >
                    <a
                        :href="item?.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="sns-hover flex size-12 items-center justify-center rounded-full bg-base-deepGrey p-3"
                        v-sendGAsignal="{
                            event_category: 'footer',
                            event_label: item?.name,
                        }"
                    >
                        <img
                            class="h-6 w-6"
                            :src="getImageSrc(item?.name)"
                            :alt="`${item?.name}`"
                        />
                    </a>
                </template>
            </section>

            <!-- content -->
            <section
                class="slt-body-1 grid grid-cols-1 gap-[26.46vw] xs:gap-[104px] lg:ml-auto lg:flex lg:w-[62.69vw] lg:items-start lg:justify-end lg:gap-[5.29vw]"
            >
                <!-- contact -->
                <div class="lg:w-[30.7vw]">
                    <div class="flex flex-col gap-14 sm:gap-16">
                        <div
                            v-for="data in footerData?.contact?.data"
                            :key="data?.name"
                            class="content-wrapper whitespace-pre-wrap"
                        >
                            <h6 class="text-base-white-50 lg:pl-4">
                                {{ data.name }}
                            </h6>
                            <ul class="grid grid-cols-1 gap-2">
                                <li
                                    v-for="item in data?.items"
                                    :key="item?.name"
                                    class="group relative cursor-pointer rounded-full text-center duration-300 hover:bg-base-dark"
                                >
                                    <a
                                        :href="
                                            item?.text?.includes('@')
                                                ? `mailto:${item?.text}`
                                                : `tel:${item?.text}`
                                        "
                                        :title="item?.name?.join('')"
                                        class="inline-grid size-full grid-cols-2 gap-4 py-1 lg:pl-4"
                                        v-sendGAsignal="{
                                            event_category: `footer.${data?.name}`,
                                            event_label: item?.name,
                                        }"
                                    >
                                        <BaseFormatText
                                            type="p"
                                            :text="item?.name"
                                            class="z-20 text-start text-base-white"
                                        />
                                        <p
                                            class="z-20 h-full text-start text-base-white"
                                            lang="en"
                                        >
                                            {{ item?.text }}
                                        </p>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <!-- sitemap -->
                <div class="content-wrapper whitespace-pre-wrap lg:w-[28vw]">
                    <h5 class="text-base-white-50">
                        {{ footerData?.sitemap?.name }}
                    </h5>
                    <ul class="columns-2">
                        <li
                            v-for="(item, index) in footerData?.sitemap?.data"
                            :key="item?.name"
                            class="info group relative mb-2 cursor-pointer break-inside-avoid"
                            @click="sitemapBindings[index].onClick"
                        >
                            <component
                                class="btn z-20 w-full"
                                :is="
                                    ruleIsLink.test(item?.href) &&
                                    item?.type !== 'linkExternal'
                                        ? NuxtLink
                                        : 'button'
                                "
                                v-bind="sitemapBindings[index].bindings"
                                data-trigger="footer"
                                v-sendGAsignal="{
                                    event_category: `footer.${footerData?.sitemap?.name}`,
                                    event_label: item?.name,
                                }"
                            >
                                <div
                                    class="z-20 flex items-center justify-center rounded-full py-1 text-left text-base-white duration-300 sm:-ml-[1rem] sm:px-[1rem] sm:group-hover:bg-base-dark"
                                >
                                    <BaseFormatText
                                        type="p"
                                        :text="item?.name"
                                    />
                                    <div
                                        class="btn__arrow__group z-20"
                                        v-if="item?.type === 'linkExternal'"
                                    >
                                        <SvgArrowOblique16
                                            :isDark="true"
                                            class="btn__arrow btn__arrow__external--main"
                                            :stroke-width="1.6"
                                        />
                                        <SvgArrowOblique16
                                            :isDark="true"
                                            class="btn__arrow btn__arrow__external--hover"
                                            :stroke-width="1.6"
                                        />
                                    </div>
                                </div>
                            </component>
                        </li>
                    </ul>
                </div>
            </section>
        </div>

        <!-- copyright -->
        <section class="relative flex items-end justify-between">
            <div class="size-[80px] sm:size-[120px]">
                <GamaGlobeLottie />
            </div>

            <p
                class="slt-h5-d mt-[120px] font-bold text-primary-orange lg:mt-[112px]"
            >
                {{ footerData?.copyright || "© 1995-2025 gamania" }}
            </p>
        </section>
        <!-- TEMP: -->
        <!-- <section
            class="fixed left-0 top-0 flex h-full w-full items-end justify-start p-[4.07vw] pt-8 sm:p-[5.2vw] sm:pt-8 lg:px-[2.64vw] lg:pb-12 lg:pt-16"
        >
            <div class="size-[80px] sm:size-[120px]">
                <GamaGlobeLottie />
            </div>
        </section> -->

        <Teleport to="body">
            <transition name="external-link-modal-fade">
                <LazyExternalLinkModal
                    v-if="isOpen"
                    :links="
                        footerData?.sitemap?.data?.filter(
                            (item) => item?.type === 'linkExternal',
                        )
                    "
                />
            </transition>
        </Teleport>
    </footer>
</template>

<style scoped>
.content-wrapper {
    @apply grid grid-cols-1 gap-y-8 sm:grid-cols-[222fr_446fr] sm:gap-5 lg:grid-cols-1 lg:grid-rows-[1fr_auto];
}

/* transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}

.external-link-modal-fade-enter-active,
.external-link-modal-fade-leave-active {
    transition: opacity 0.5s ease;
}
.external-link-modal-fade-enter-from,
.external-link-modal-fade-leave-to {
    opacity: 0;
}

.external-link-modal-fade-enter-to,
.external-link-modal-fade-leave-from {
    opacity: 1;
}

.sns-hover {
    @apply duration-100 ease-in-out-cubic sm:hover:scale-110;
}

/* Button Arrow Animation */
.btn {
    @apply flex size-full items-center gap-1 text-base-white;
}
.btn__arrow {
    @apply absolute right-0 top-0;
    transition:
        transform 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33),
        opacity 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33);
}
.btn__arrow__group {
    @apply relative ml-1 inline-block size-[4.07vw] overflow-hidden align-middle xs:size-4 sm:size-[2.08vw] lg:size-[1.06vw] 3xl:size-[20.3px];
}
.btn__arrow {
    @apply absolute right-0 top-0 size-full;
    @apply absolute right-0 top-0 size-full;
}
.btn__arrow__external--hover {
    transform: translate3d(-100%, 100%, 0);
    opacity: 0;
}

@media screen and (min-width: 640px) {
    .info:hover .btn__arrow__external--main {
        transform: translate3d(100%, -100%, 0);
        opacity: 0;
    }
    .info:hover .btn__arrow__external--hover {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}
</style>
